import { apiClient } from '@/utils/api-client';
import type {
  Distribution,
  DistributionListResponse,
  DistributionCreateRequest,
  DistributionCreateResponse,
  DistributionUpdateRequest,
  DistributionFinalizeResponse,
  DistributionNotice,
  AvailableAssetSale,
  WaterfallCalculationResponse,
  RawDistribution,
  RawDistributionListResponse,
  RawAvailableAssetSale,
  RawWaterfallCalculationResponse,
} from '@/types/distribution';
import type { FundTransactionDetail } from '@/types/fund-transactions';
import {
  mapDistribution,
  mapDistributionListResponse,
  mapAvailableAssetSale,
  mapWaterfallCalculationResponse,
} from './distributionMapper';

export type {
  DistributionListResponse,
  DistributionCreateRequest,
  DistributionCreateResponse,
  DistributionUpdateRequest,
  DistributionFinalizeResponse,
  DistributionNotice,
  AvailableAssetSale,
};

export const distributionService = {
  async getDistributions(
    fundId: string,
    params?: {
      page?: number;
      page_size?: number;
      status?: string;
      search?: string;
    }
  ): Promise<DistributionListResponse> {
    const raw = await apiClient.get<RawDistributionListResponse>(
      `/funds/${fundId}/distributions`,
      { params }
    );
    return mapDistributionListResponse(raw);
  },

  async getDistribution(
    fundId: string,
    distributionId: string
  ): Promise<Distribution> {
    const raw = await apiClient.get<RawDistribution>(
      `/funds/${fundId}/distributions/${distributionId}`
    );
    return mapDistribution(raw);
  },

  async getNotices(
    fundId: string,
    distributionId: string
  ): Promise<DistributionNotice[]> {
    return await apiClient.get(
      `/funds/${fundId}/distributions/${distributionId}/notices`
    );
  },

  async createDistribution(
    fundId: string,
    data: DistributionCreateRequest
  ): Promise<DistributionCreateResponse> {
    return await apiClient.post(`/funds/${fundId}/distributions`, data);
  },

  async updateDistribution(
    fundId: string,
    distributionId: string,
    data: DistributionUpdateRequest
  ): Promise<Distribution> {
    return await apiClient.patch(
      `/funds/${fundId}/distributions/${distributionId}`,
      data
    );
  },

  async getAvailableAssetSales(fundId: string): Promise<AvailableAssetSale[]> {
    const raw = await apiClient.get<RawAvailableAssetSale[]>(
      `/funds/${fundId}/distributions/available-asset-sales`
    );
    return raw.map(mapAvailableAssetSale);
  },

  async calculateWaterfall(
    fundId: string,
    distributableAmount: number,
    assetSaleTransactionIds?: string[]
  ): Promise<WaterfallCalculationResponse> {
    const raw = await apiClient.post<RawWaterfallCalculationResponse>(
      `/funds/${fundId}/distributions/calculate-waterfall`,
      {
        amount: distributableAmount,
        asset_sale_transaction_ids: assetSaleTransactionIds ?? [],
      }
    );
    return mapWaterfallCalculationResponse(raw);
  },

  async finalizeDistribution(
    fundId: string,
    distributionId: string
  ): Promise<DistributionFinalizeResponse> {
    return await apiClient.post(
      `/funds/${fundId}/distributions/${distributionId}/finalize`
    );
  },

  async regenerateNotices(
    fundId: string,
    distributionId: string
  ): Promise<DistributionFinalizeResponse> {
    return await apiClient.post(
      `/funds/${fundId}/distributions/${distributionId}/regenerate-notices`
    );
  },

  async resendNotices(
    fundId: string,
    distributionId: string
  ): Promise<DistributionFinalizeResponse> {
    return await apiClient.post(
      `/funds/${fundId}/distributions/${distributionId}/resend-notices`
    );
  },

  async getFundTransaction(
    fundId: string,
    distributionId: string
  ): Promise<FundTransactionDetail> {
    return await apiClient.get(
      `/funds/${fundId}/distributions/${distributionId}/fund-transaction`
    );
  },
};
